import { DestroyRef, Injectable, inject } from '@angular/core';
import { qpxToast as toast } from '@quipex/core/utils';
import type { ExternalToast } from 'ngx-sonner';

type ActivityToastId = string | number;

export interface ActivityToastScope {
    readonly isCurrent: () => boolean;
    createChild(): ActivityToastScope;
    disown(id: ActivityToastId): void;
    dismiss(id: ActivityToastId): void;
    guard(callback: () => void): () => void;
    own(id: ActivityToastId): ActivityToastId;
    release(): void;
    destroy(): void;
}

@Injectable()
export class ActivityToastOwner {
    private readonly destroyRef = inject(DestroyRef);
    private readonly scopes = new Set<OwnedActivityToastScope>();

    public constructor() {
        this.destroyRef.onDestroy(() => this.invalidateAll());
    }

    public createScope(
        isValid: () => boolean = () => true
    ): ActivityToastScope {
        return this.createOwnedScope(isValid);
    }

    private createOwnedScope(
        isValid: () => boolean,
        parent?: OwnedActivityToastScope
    ): OwnedActivityToastScope {
        const scope = new OwnedActivityToastScope(
            isValid,
            (ownerScope) =>
                this.createOwnedScope(ownerScope.isCurrent, ownerScope),
            (releasedScope) => {
                parent?.removeChild(releasedScope);
                this.scopes.delete(releasedScope);
            }
        );
        this.scopes.add(scope);
        parent?.addChild(scope);
        return scope;
    }

    public invalidateAll(): void {
        for (const scope of [...this.scopes]) scope.destroy();
    }
}

class OwnedActivityToastScope implements ActivityToastScope {
    private readonly children = new Set<OwnedActivityToastScope>();
    private readonly ids = new Set<ActivityToastId>();
    private current = true;

    public constructor(
        private readonly isValid: () => boolean,
        private readonly createOwnedChild: (
            ownerScope: OwnedActivityToastScope
        ) => OwnedActivityToastScope,
        private readonly releaseOwnedScope: (
            releasedScope: OwnedActivityToastScope
        ) => void
    ) {}

    public readonly isCurrent = (): boolean => this.current && this.isValid();

    public createChild(): ActivityToastScope {
        return this.createOwnedChild(this);
    }

    public own(id: ActivityToastId): ActivityToastId {
        if (this.isCurrent()) this.ids.add(id);
        else toast.dismiss(id);
        return id;
    }

    public dismiss(id: ActivityToastId): void {
        this.ids.delete(id);
        toast.dismiss(id);
    }

    public disown(id: ActivityToastId): void {
        this.ids.delete(id);
    }

    public guard(callback: () => void): () => void {
        return () => {
            if (this.isCurrent()) callback();
        };
    }

    public release(): void {
        this.finish(false);
    }

    public destroy(): void {
        this.finish(true);
    }

    public addChild(scope: OwnedActivityToastScope): void {
        this.children.add(scope);
    }

    public removeChild(scope: OwnedActivityToastScope): void {
        this.children.delete(scope);
    }

    private finish(dismiss: boolean): void {
        if (!this.current) return;
        this.current = false;
        for (const child of [...this.children]) child.destroy();
        if (dismiss) {
            for (const id of this.ids) toast.dismiss(id);
        }
        this.ids.clear();
        this.releaseOwnedScope(this);
    }
}

export function activityToastLifecycle(
    scope: ActivityToastScope
): Pick<ExternalToast, 'onAutoClose' | 'onDismiss'> {
    return {
        onAutoClose: () => scope.release(),
        onDismiss: () => scope.release(),
    };
}
